import { Box, Flex } from '@chakra-ui/react';
import { useState } from 'react';

import { StackedBarTooltip } from '@revelio/replots';

import { fmtUSD } from '../format';
import {
  DISPOSITION_DIRECT,
  DISPOSITION_INDIRECT,
  DISPOSITION_INDUCED,
  type Disposition,
} from '../useImpact';

// Direct/indirect/induced palette from the design handoff (chart-1/2/3).
export const DII_COLOR: Record<Disposition, string> = {
  [DISPOSITION_DIRECT]: '#5ca9e9',
  [DISPOSITION_INDIRECT]: '#70ca97',
  [DISPOSITION_INDUCED]: '#e8a18a',
};
export const DII_LABEL: Record<Disposition, string> = {
  [DISPOSITION_DIRECT]: 'Direct',
  [DISPOSITION_INDIRECT]: 'Indirect',
  [DISPOSITION_INDUCED]: 'Induced',
};
export const DISPOSITIONS = [
  DISPOSITION_DIRECT,
  DISPOSITION_INDIRECT,
  DISPOSITION_INDUCED,
] as const;

export interface DiiTotals {
  direct: number;
  indirect: number;
  induced: number;
}

export const diiTotal = (d: DiiTotals) => d.direct + d.indirect + d.induced;

export interface DiiRow extends DiiTotals {
  /** Stable identity (industry name or county fips) for cross-highlight. */
  key: string;
  label: string;
}

// Cap a long industry/county name so the tooltip header stays one line next to
// the "N counties" annotation (the bar's own row label ellipsizes the same way).
const TITLE_MAX = 30;
const truncateTitle = (label: string) =>
  label.length > TITLE_MAX
    ? `${label.slice(0, TITLE_MAX - 1).trimEnd()}…`
    : label;

export function DiiLegend() {
  return (
    <Flex gap="16px">
      {DISPOSITIONS.map((disposition) => (
        <Flex
          key={disposition}
          align="center"
          gap="6px"
          fontSize="11.5px"
          fontWeight="500"
          color="navyBlue.500"
        >
          <Box
            w="11px"
            h="11px"
            borderRadius="3px"
            bg={DII_COLOR[disposition]}
          />
          {DII_LABEL[disposition]}
        </Flex>
      ))}
    </Flex>
  );
}

interface StackedDiiBarsProps {
  rows: DiiRow[];
  labelWidth?: number;
  /** Rows whose key is highlighted elsewhere (e.g. map hover) get a green wash. */
  externalKeys?: string[];
  onRowHover?: (row: DiiRow) => void;
  onRowLeave?: () => void;
  /** Per-row contributor county count (industries chart) → "N counties". */
  countByKey?: ReadonlyMap<string, number>;
}

// Ranked horizontal bars, each split into direct/indirect/induced on a shared
// scale (design StackedDIIBars). Hovering a row shows a tooltip with that row's
// D/I/I breakdown (and, when given, how many counties the industry touches) and
// fires onRowHover for the map cross-highlight. The full-width bar lane is the
// hover target, so even near-zero bars stay reachable.
export function StackedDiiBars({
  rows,
  labelWidth = 210,
  externalKeys = [],
  onRowHover,
  onRowLeave,
  countByKey,
}: StackedDiiBarsProps) {
  const [hoveredKey, setHoveredKey] = useState<string | null>(null);
  const max = Math.max(1, ...rows.map((r) => diiTotal(r)));

  return (
    <Flex direction="column" gap="6px" data-testid="dii-bars">
      {rows.map((row) => {
        const total = diiTotal(row);
        const on = hoveredKey === row.key || externalKeys.includes(row.key);
        const count = countByKey?.get(row.key);
        return (
          <Flex
            key={row.key}
            align="center"
            gap="10px"
            px="6px"
            mx="-6px"
            py="4px"
            borderRadius="5px"
            bg={on ? 'green.50' : 'transparent'}
            cursor={onRowHover ? 'pointer' : 'default'}
            transition="background 120ms"
            onMouseEnter={() => {
              setHoveredKey(row.key);
              onRowHover?.(row);
            }}
            onMouseLeave={() => {
              setHoveredKey(null);
              onRowLeave?.();
            }}
            data-testid="dii-bar-row"
          >
            <Box
              w={`${labelWidth}px`}
              flexShrink={0}
              minW="0"
              fontSize="12.5px"
              color="navyBlue.500"
              noOfLines={1}
            >
              {row.label}
            </Box>
            <StackedBarTooltip
              title={truncateTitle(row.label)}
              titleRight={
                count === undefined
                  ? undefined
                  : `${count} ${count === 1 ? 'county' : 'counties'}`
              }
              entries={DISPOSITIONS.map((d) => ({
                label: DII_LABEL[d],
                active: false,
                mainValue: fmtUSD(row[d]),
              }))}
            >
              {/* The full-width lane is the hover target so near-zero bars
                  remain reachable. */}
              <Box flex="1" h="18px" display="flex">
                <Flex
                  width={`${(total / max) * 100}%`}
                  h="100%"
                  gap="1px"
                  transition="width 0.45s ease"
                >
                  {DISPOSITIONS.map((d) => (
                    <Box
                      key={d}
                      width={`${total > 0 ? (row[d] / total) * 100 : 0}%`}
                      h="100%"
                      bg={DII_COLOR[d]}
                      data-testid={`dii-segment-${d}`}
                    />
                  ))}
                </Flex>
              </Box>
            </StackedBarTooltip>
            <Box
              w="66px"
              flexShrink={0}
              textAlign="right"
              fontFamily="mono"
              fontWeight="600"
              fontSize="12px"
              color="navyBlue.500"
            >
              {fmtUSD(total)}
            </Box>
          </Flex>
        );
      })}
    </Flex>
  );
}
